import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import { Form } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import { CreateUpdateInvoiceIssuerDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/CreateUpdateInvoiceIssuerDto';

type TextFieldKey = Exclude<
  keyof CreateUpdateInvoiceIssuerDto,
  'krd' | 'active' | 'isDefault'
>;

const textFields: { key: TextFieldKey; label: string; textarea?: boolean }[] = [
  { key: 'name', label: 'Nazwa' },
  { key: 'address', label: 'Adres' },
  { key: 'bankName', label: 'Nazwa banku' },
  { key: 'bankAccount', label: 'Numer konta' },
  { key: 'symbol', label: 'Symbol faktury' },
  { key: 'symbolCorrection', label: 'Symbol korekty' },
  { key: 'note', label: 'Uwaga na fakturze', textarea: true },
  { key: 'footer', label: 'Stopka faktury', textarea: true },
];

interface InvoiceIssuerEditorProps {
  id?: string;
  nip?: string | null;
  issuer: CreateUpdateInvoiceIssuerDto;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    issuer: CreateUpdateInvoiceIssuerDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const InvoiceIssuerEditor: React.FC<
  InvoiceIssuerEditorProps
> = props => {
  const [issuer, setIssuer] = useState(props.issuer);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setIssuer(props.issuer);
    }
  }, [props.show, props.issuer, props.id]);

  const propertyChange = (obj: Partial<CreateUpdateInvoiceIssuerDto>) => {
    setIssuer({ ...issuer, ...obj });
  };

  const handleSave = async () => {
    props
      .onCompleted(issuer, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
        }
      })
      .catch(error => {
        setSaveErrors(error);
        setShowSaveErrors(true);
      });
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {props.id ? 'Edycja wystawcy faktur' : 'Nowy wystawca faktur'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card className="mb-2">
          <Card.Header>Dane wystawcy</Card.Header>
          <Card.Body>
            <Form className="d-grid gap-3">
              <FormRow controlId="nip" label="NIP">
                {/* NIP niezmienny po utworzeniu (klucz certyfikatu KSeF) */}
                {props.id ? (
                  <Form.Control type="text" value={props.nip || ''} disabled />
                ) : (
                  <Form.Control
                    type="text"
                    value={issuer?.nip || ''}
                    onChange={e => propertyChange({ nip: e.target.value })}
                  />
                )}
              </FormRow>
              {textFields.map(field => (
                <FormRow
                  key={field.key}
                  controlId={field.key}
                  label={field.label}
                >
                  <Form.Control
                    type="text"
                    as={field.textarea ? 'textarea' : 'input'}
                    {...(field.textarea && { rows: 2 })}
                    value={issuer?.[field.key] || ''}
                    onChange={e =>
                      propertyChange({ [field.key]: e.target.value })
                    }
                  />
                </FormRow>
              ))}
              <FormRow controlId="krd" label="Logo KRD na fakturze">
                <Form.Check
                  type="checkbox"
                  checked={!!issuer?.krd}
                  onChange={e => propertyChange({ krd: e.target.checked })}
                />
              </FormRow>
              <FormRow controlId="isDefault" label="Domyślny">
                <Form.Check
                  type="checkbox"
                  checked={!!issuer?.isDefault}
                  onChange={e =>
                    propertyChange({ isDefault: e.target.checked })
                  }
                />
              </FormRow>
              <FormRow controlId="active" label="Aktywny">
                <Form.Check
                  type="checkbox"
                  checked={!!issuer?.active}
                  onChange={e => propertyChange({ active: e.target.checked })}
                />
              </FormRow>
            </Form>
          </Card.Body>
        </Card>
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="mt-3"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={props.handleClose}>
          Zamknij
        </Button>
        <Button variant="outline-primary" onClick={handleSave}>
          Zapisz
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
